import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Sticker — a round sticker slapped onto the sheet for wins, level-ups and payouts.
 *
 * `slap` plays the slap-down animation once on mount (CSS; skipped under reduced motion, where
 * the sticker simply appears). Pair it with useFx().celebrate(...) for confetti + sound, and
 * always keep the visible text (`label`) — the sticker IS the text state.
 * `tint` takes any CSS colour so Arcade games can use their own palette.
 */
export interface StickerProps extends React.HTMLAttributes<HTMLDivElement> {
  label: React.ReactNode
  /** Small line above the label (e.g. "Level", "+50 RB"). */
  kicker?: React.ReactNode
  icon?: React.ReactNode
  slap?: boolean
  tint?: string
  size?: 'sm' | 'md' | 'lg'
  /** Resting tilt in degrees. Default -9. */
  tilt?: number
}

export const Sticker = React.forwardRef<HTMLDivElement, StickerProps>(function Sticker(
  { label, kicker, icon, slap = false, tint, size = 'md', tilt, className, style, ...props },
  ref,
) {
  const vars = {
    ...(tint ? { '--sticker-tint': tint } : {}),
    ...(tilt !== undefined ? { '--sticker-tilt': `${tilt}deg` } : {}),
    ...style,
  } as React.CSSProperties
  // Announce as one phrase ("Level 5") when the parts are plain text; otherwise let the
  // children speak for themselves.
  const text = [kicker, label].filter((p) => typeof p === 'string' || typeof p === 'number').join(' ')
  const hide = text ? true : undefined
  return (
    <div
      ref={ref}
      role={text ? 'img' : undefined}
      aria-label={text || undefined}
      data-size={size}
      data-slap={slap || undefined}
      className={cn('v2-sticker', className)}
      style={vars}
      {...props}
    >
      {icon && <span className="v2-sticker-icon" aria-hidden="true">{icon}</span>}
      {kicker && <span className="v2-sticker-kicker" aria-hidden={hide}>{kicker}</span>}
      <span className="v2-sticker-label" aria-hidden={hide}>{label}</span>
    </div>
  )
})
